/**
 * Create the Lost Report Search Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createLostReportSearchAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'lost_report_search_agent',
    name: 'Lost Report Search Agent',
    description: 'Step 3: Date range picker and search execution',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt to trigger showing date picker
    initialPrompt: 'The license data has been confirmed. Call show_date_picker to display the date range selection UI. Guide the user to select a start and end date around when their accident happened, then click Search.',
    
    entryGuardrails: requireLanguage(),
    
    exitGuardrails: {
      transitions: new Map([
        ['search_complete', {
          target: { agentId: 'lost_report_result_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['restart', {
          target: { agentId: 'lost_report_upload_extract_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['cancel', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'show_date_picker') {
        console.log('[LostReportSearchAgent] Showing date picker');
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: 'Date picker is now visible. User can select date range and search.',
          },
        };
      }
      
      if (toolName === 'search_complete') {
        const count = args.count || 0;
        const startDate = args.start_date;
        const endDate = args.end_date;
        
        console.log('[LostReportSearchAgent] Search complete:', { count, startDate, endDate });
        
        return {
          toolName,
          args,
          result: {
            success: true,
            count,
            message: `Found ${count} reports`,
          },
          triggersTransition: true,
          transitionTo: 'lost_report_result_agent',
          transitionData: {
            searchResults: {
              count,
              startDate,
              endDate,
            },
          },
        };
      }
      
      if (toolName === 'restart_flow') {
        console.log('[LostReportSearchAgent] Restarting flow');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Restarting from upload' },
          triggersTransition: true,
          transitionTo: 'lost_report_upload_extract_agent',
          transitionData: {
            licenseData: undefined,
            searchResults: undefined,
          },
        };
      }
      
      if (toolName === 'cancel_lost_report') {
        console.log('[LostReportSearchAgent] User cancelled');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Cancelled' },
          triggersTransition: true,
          transitionTo: 'router_agent',
          transitionData: {
            licenseData: undefined,
            searchResults: undefined,
          },
        };
      }
      
      // Inline FAQ
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[LostReportSearchAgent] Inline FAQ:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: 'Answer the question, then guide user to select dates.',
          },
          triggersTransition: false,
        };
      }
      
      // Universal navigation: Switch to accident reporting
      if (toolName === 'start_accident_reporting') {
        console.log('[LostReportSearchAgent] Switching to accident reporting');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to accident reporting' },
          triggersTransition: true,
          transitionTo: 'accident_start_agent',
        };
      }
      
      // Universal navigation: Switch to renewal
      if (toolName === 'start_renewal') {
        console.log('[LostReportSearchAgent] Switching to renewal');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to renewal' },
          triggersTransition: true,
          transitionTo: 'renewal_input_agent',
        };
      }
      
      // Universal navigation: Switch to FAQ
      if (toolName === 'go_to_faq') {
        console.log('[LostReportSearchAgent] Switching to FAQ');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to FAQ' },
          triggersTransition: true,
          transitionTo: 'faq_agent',
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      console.log('[LostReportSearchAgent] Activated - Step 3 of lost report flow');
      console.log('  License data:', context.transitionData.licenseData);
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[LostReportSearchAgent] Deactivated');
    },
  };
}

export default createLostReportSearchAgent;

